import { Component, OnInit } from '@angular/core';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../support-contact';
import { Router } from '@angular/router';
import { consumeIntendedUrl } from '../auth/return-to';
import { UserAuthService } from '../UserAuth.service';
import { CustomerService } from '../services/customer.service';
import { dedupeCompanies } from '../services/available-companies.service';
import { ActiveCustomer } from '../models/active-customer';

@Component({
  selector: 'app-customer-selector',
  templateUrl: './customer-selector.component.html',
  styleUrls: ['./customer-selector.component.scss'],
})
export class CustomerSelectorComponent implements OnInit {
  customers: ActiveCustomer[] = [];
  // Selection key is the composite `${CustomerId}|${TenantId}` — the SAME CustomerId
  // can appear under multiple tenants, so CustomerId alone is ambiguous and would
  // always resolve to the first match (see keyFor / onContinue).
  selectedKey = '';
  searchTerm = '';
  isLoading = true;
  errorMessage = '';
  // Names that occur under more than one tenant — those get a tenant discriminator in the UI.
  private duplicatedNames = new Set<string>();

  /**
   * True when NO available company belongs to the tenant the user signed in with.
   *
   * Deliberately a distinct state from "still choosing": it means every option is
   * a cross-tenant one, so an ordinary user is about to be refused whatever they
   * pick, and should be told that now rather than after the first API call.
   */
  noTenantMatch = false;

  /**
   * An External ID session with several bound customers. The picker renders as an
   * open list so every customer is visible without a click; nothing is preselected,
   * because list order is not a reason to choose one of their customers.
   */
  externalList = false;

  /** Shown in the mismatch notice — see support-contact.ts for the no-hours rule. */
  readonly supportEmail = SUPPORT_EMAIL;
  readonly supportPhone = SUPPORT_PHONE;

  constructor(
    private authService: UserAuthService,
    private customerService: CustomerService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.authService.getActiveCustomer()) {
      this.router.navigate(['/']);
      return;
    }

    const objectId = this.authService.getObjectId();
    const tenantId = this.authService.CustomerTenantId;

    this.customerService.getCustomers(objectId, tenantId).subscribe({
      next: (list) => {
        this.isLoading = false;
        this.customers = dedupeCompanies(list);
        this.duplicatedNames = this.findDuplicatedNames(this.customers);
        this.authService.setAvailableCustomers(this.customers);

        if (this.customers.length === 0) {
          this.errorMessage = 'No customers are assigned to your account. Contact your administrator.';
          return;
        }

        // AN EXTERNAL ID (CIAM) SESSION SKIPS THE TENANT CHECK BELOW.
        //
        // getSignedInTenantId() reads the WORKFORCE MSAL instance only, so for a
        // CIAM session it is '' and nothing ever matched: Joel's first External
        // sign-in (2026-10-07) got the "None of your companies belong to the
        // organisation" warning over a blank picker. Nor would the CIAM tenant
        // help if it were read: every External customer shares it, so it says
        // nothing about which customer is theirs. The server already scoped this
        // list to the caller's own customerIds bindings, so every row IS theirs.
        // One row: nothing to choose, go straight in. Several: show them all
        // (see externalList in the template) and let them pick.
        if (this.authService.isExternalIdAuthenticated()) {
          this.externalList = true;
          if (this.customers.length === 1) {
            this.authService.setActiveCustomer(this.customers[0]);
            this.goToIntendedDestination();
          }
          return;
        }

        // THE DEFAULT IS THE SIGNED-IN TENANT, NOT LIST ORDER.
        //
        // This used to take customers[0], which is whatever Cosmos happened to
        // return first. Joel signed in as joel@vericure.io (tenant c46c90fe) and
        // landed on the 83310ec5 row, and every call then carried a tenant he is
        // not a member of — AlertsApi's TenantAccess.Resolve compares the user
        // document's tenantId against the query tenantId, so /v1/alerts/summary
        // answered "User is not authorized for this tenant". The tenant he
        // AUTHENTICATED against is the only defensible default; anything else is a
        // guess dressed as a default.
        //
        // (83310ec5 is saswattech.in, which has zero users. It survived the purge,
        // so it is a row the client can offer that nobody can authenticate against.)
        const signedInTenant = this.authService.getSignedInTenantId();
        const match = signedInTenant
          ? this.customers.find((c) => this.sameTenant(c.TenantId, signedInTenant))
          : undefined;

        if (match) {
          this.selectedKey = this.keyFor(match);
          // One company AND it is the tenant they signed in with: nothing to
          // choose between, so go straight in.
          if (this.customers.length === 1) {
            this.authService.setActiveCustomer(match);
            this.goToIntendedDestination();
          }
          return;
        }

        // NO MATCH — DO NOT FALL BACK TO [0]. That is the bug, and it would come
        // back under a condition nobody tests. Leave the picker unselected and say
        // why. A SuperAdmin legitimately operates across tenants and can still
        // choose any row deliberately; an ordinary user learns immediately that
        // none of their companies belongs to the tenant they signed in with,
        // rather than discovering it as an authorization error three screens later.
        this.selectedKey = '';
        this.noTenantMatch = true;
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage =
          'Failed to load customers. Please try again, or contact Vericure Support at ' +
          `${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`;
      },
    });
  }

  /** Tenant GUIDs compare case-insensitively; Entra is not consistent about casing. */
  private sameTenant(a?: string | null, b?: string | null): boolean {
    return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  }

  /** Composite identity for a customer row: a CustomerId can exist under several tenants. */
  keyFor(c: ActiveCustomer): string {
    return `${c.CustomerId}|${c.TenantId}`;
  }

  /**
   * Display label. Plain CustomerName when it is unique (the common single-tenant case);
   * suffixed with a short tenant discriminator when the same name spans multiple tenants,
   * so an operator can pick THEIR tenant deliberately rather than by list order.
   */
  labelFor(c: ActiveCustomer): string {
    if (this.duplicatedNames.has(c.CustomerName)) {
      const discriminator = c.TenantName || `tenant ${c.TenantId.slice(0, 8)}`;
      return `${c.CustomerName} — ${discriminator}`;
    }
    return c.CustomerName;
  }

  /** Drop rows that are identical on (CustomerId, TenantId) — genuine duplicates carry no new choice. */
  private findDuplicatedNames(list: ActiveCustomer[]): Set<string> {
    const counts = new Map<string, number>();
    for (const c of list) {
      counts.set(c.CustomerName, (counts.get(c.CustomerName) ?? 0) + 1);
    }
    const dups = new Set<string>();
    for (const [name, n] of counts) {
      if (n > 1) dups.add(name);
    }
    return dups;
  }

  /** Rows the open list shows at once; more than this scroll inside it. */
  get listSize(): number {
    return Math.min(Math.max(this.customers.length, 2), 8);
  }

  get useSearch(): boolean {
    return this.customers.length > 20;
  }

  get filteredCustomers(): ActiveCustomer[] {
    if (!this.searchTerm.trim()) return this.customers;
    const term = this.searchTerm.toLowerCase();
    return this.customers.filter((c) => c.CustomerName.toLowerCase().includes(term));
  }

  onContinue(): void {
    const customer = this.customers.find((c) => this.keyFor(c) === this.selectedKey);
    if (!customer) return;
    console.log('[CustomerSelector] Setting active customer:', customer);
    this.authService.setActiveCustomer(customer);
    localStorage.removeItem('lastSelectedProject');
    this.goToIntendedDestination();
  }

  /**
   * Continue to wherever the user was actually going.
   *
   * A deep link into /admin/drums from a fresh tab correctly detours through this
   * picker — and used to be dropped at /projects afterwards, so a shared admin
   * link only worked for someone who already had a customer selected.
   *
   * The stored destination is SINGLE-USE (consume, not peek): leaving it behind
   * means the next unrelated navigation gets pulled back to a stale target, which
   * reads as the app jumping somewhere nobody asked for.
   */
  private goToIntendedDestination(): void {
    const intended = consumeIntendedUrl();
    this.router.navigateByUrl(intended ?? '/');
  }
}
